import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { X, Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Liste complète des vitamines disponibles pour protocole personnalisé
const VITAMINES_DISPONIBLES = [
  // === PRODUITS MULTI-VITAMINES ===
  'Cernevit (Multivitamines IV - A, B1, B2, B3, B5, B6, B7, B9, B12, C, D3, E)',
  // === VITAMINES ===
  'Vitamine A (Rétinol)', 'Bêta-Carotène',
  'Vitamine C (Acide ascorbique)', 'Vitamine C haute dose (10-25g)', 'Vitamine C mega dose (>25g)',
  'Vitamine B1 (Thiamine)', 'Vitamine B2 (Riboflavine)', 'Vitamine B3 (Niacine/Niacinamide)',
  'Vitamine B5 (Acide pantothénique)', 'Vitamine B6 (Pyridoxine)', 'Vitamine B7 (Biotine)',
  'Vitamine B9 (Acide folique)', 'Vitamine B12 (Cobalamine/Cyanocobalamine)',
  'Vitamine B12 (Méthylcobalamine)', 'Vitamine B12 (Hydroxocobalamine)', 'Vitamines B Complex (B1-B12)',
  'Vitamine D2 (Ergocalciférol)', 'Vitamine D3 (Cholécalciférol)',
  'Vitamine E (Tocophérol)', 'Vitamine E mixte (Tocophérols)',
  'Vitamine K1 (Phylloquinone)', 'Vitamine K2 (Ménaquinone)',
  // === MINÉRAUX ===
  'Calcium (Gluconate)', 'Calcium (Chlorure)', 'Magnésium (Sulfate)', 'Magnésium (Chlorure)',
  'Potassium (Chlorure)', 'Sodium (Chlorure)', 'Phosphore', 'Zinc (Sulfate)', 'Zinc (Gluconate)',
  'Fer (Gluconate)', 'Fer (Sucrose)', 'Cuivre (Gluconate)', 'Manganèse (Sulfate)',
  'Chrome (Chlorure)', 'Sélénium (Sélénite)', 'Molybdène', 'Iode (Iodure)',
  // === ANTIOXYDANTS ===
  'Glutathion réduit (GSH)', 'Glutathion liposomal', 'N-Acétylcystéine (NAC)',
  'Acide Alpha-Lipoïque (ALA)', 'Coenzyme Q10 (CoQ10)', 'Ubiquinol',
  // === ACIDES AMINÉS ===
  'L-Arginine', 'L-Carnitine', 'L-Carnosine', 'L-Glutamine', 'L-Glutathion',
  'L-Glycine', 'L-Taurine', 'L-Tryptophane', 'L-Tyrosine',
  // === AUTRES ===
  'Collagène hydrolysé', 'NAD+', 'NADH', 'Phosphatidylcholine',
  'Oméga-3 (EPA/DHA)', 'Choline', 'Inositol', 'MSM', 'Acide Hyaluronique',
  'Autre (à préciser)'
].sort();

const InfusionFormDialog = ({ open, onClose, onSuccess, infusion }) => {
  const [patients, setPatients] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedProtocole, setSelectedProtocole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProtocols, setLoadingProtocols] = useState(true);
  
  // État pour le protocole personnalisé
  const [customProtocole, setCustomProtocole] = useState({
    nom: '',
    vitamines: [],
    dosage: 'Standard',
    debit: '125',
    duree: 45
  });
  const [searchVitamine, setSearchVitamine] = useState('');
  const [protocoleMode, setProtocoleMode] = useState('predefined');

  const isEditing = !!infusion;

  useEffect(() => {
    if (open) {
      fetchPatients();
      fetchProtocols();
      
      if (infusion) {
        setSelectedPatient(infusion.patient_id);
        setSelectedProtocole(infusion.protocole);
        setProtocoleMode('predefined');
      } else {
        setSelectedPatient('');
        setSelectedProtocole(null);
        setCustomProtocole({
          nom: '',
          vitamines: [],
          dosage: 'Standard',
          debit: '125',
          duree: 45
        });
        setProtocoleMode('predefined');
      }
    }
  }, [open, infusion]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchProtocols = async () => {
    setLoadingProtocols(true);
    try {
      const response = await axios.get(`${API}/protocols`);
      // Filtrer seulement les protocoles actifs
      const activeProtocols = response.data.filter(p => p.statut === 'actif');
      setProtocols(activeProtocols);
    } catch (error) {
      console.error('Error fetching protocols:', error);
      toast.error('Erreur lors du chargement des protocoles');
    } finally {
      setLoadingProtocols(false);
    }
  };

  const addVitamine = (vitamine) => {
    if (vitamine && !customProtocole.vitamines.includes(vitamine)) {
      setCustomProtocole({
        ...customProtocole,
        vitamines: [...customProtocole.vitamines, vitamine]
      });
      setSearchVitamine('');
    }
  };

  const removeVitamine = (vitamine) => {
    setCustomProtocole({
      ...customProtocole,
      vitamines: customProtocole.vitamines.filter(v => v !== vitamine)
    });
  };

  const filteredVitamines = VITAMINES_DISPONIBLES.filter(v => 
    v.toLowerCase().includes(searchVitamine.toLowerCase()) &&
    !customProtocole.vitamines.includes(v)
  );

  const handleSubmit = async () => {
    let protocoleToSend;
    
    if (protocoleMode === 'custom') {
      if (!customProtocole.nom.trim()) {
        toast.error('Veuillez donner un nom au protocole personnalisé');
        return;
      }
      if (customProtocole.vitamines.length === 0) {
        toast.error('Veuillez ajouter au moins une vitamine');
        return;
      }
      
      protocoleToSend = {
        ...customProtocole,
        debit: `${customProtocole.debit} mL/h`
      };
    } else {
      if (!selectedProtocole) {
        toast.error('Veuillez sélectionner un protocole');
        return;
      }
      protocoleToSend = selectedProtocole;
    }

    if (!selectedPatient) {
      toast.error('Veuillez sélectionner un patient');
      return;
    }

    setLoading(true);

    try {
      if (isEditing) {
        await axios.put(`${API}/infusions/${infusion.id}`, {
          patient_id: selectedPatient,
          protocole: protocoleToSend
        });
        toast.success('✅ Perfusion modifiée avec succès');
      } else {
        await axios.post(`${API}/infusions`, {
          patient_id: selectedPatient,
          protocole: protocoleToSend
        });
        toast.success('✅ Perfusion créée avec succès');
      }

      onSuccess();
      setSelectedPatient('');
      setSelectedProtocole(null);
      setCustomProtocole({
        nom: '',
        vitamines: [],
        dosage: 'Standard',
        debit: '125',
        duree: 45
      });
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} infusion:`, error);
      toast.error(`Erreur lors de ${isEditing ? 'la modification' : 'la création'} de la perfusion`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="infusion-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifier la Perfusion IV' : 'Planifier une Perfusion IV'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Modifiez le patient ou le protocole de perfusion'
              : 'Sélectionnez un patient et un protocole de vitaminothérapie'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Patient Selection */}
          <div className="space-y-2">
            <Label>Patient *</Label>
            <select
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="infusion-patient-select"
            >
              <option value="">Sélectionner un patient</option>
              {patients.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.identite.prenom} {patient.identite.nom}
                </option>
              ))}
            </select>
          </div>

          {/* Protocol Selection with Tabs */}
          <div className="space-y-3">
            <Label>Protocole de Perfusion *</Label>
            <Tabs value={protocoleMode} onValueChange={setProtocoleMode}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="predefined">Protocoles Prédéfinis</TabsTrigger>
                <TabsTrigger value="custom">Protocole Personnalisé</TabsTrigger>
              </TabsList>
              
              {/* Protocoles prédéfinis - depuis la base de données */}
              <TabsContent value="predefined" className="mt-4">
                {loadingProtocols ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : protocols.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center py-8 text-gray-500">
                        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="font-medium">Aucun protocole disponible</p>
                        <p className="text-sm mt-2">
                          Créez des protocoles dans Paramètres → Protocoles
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {protocols.map((protocole) => (
                      <Card
                        key={protocole.id}
                        className={`cursor-pointer transition-all ${
                          selectedProtocole?.id === protocole.id || selectedProtocole?.nom === protocole.nom
                            ? 'ring-2 ring-blue-500 bg-blue-50'
                            : 'hover:shadow-md'
                        }`}
                        onClick={() => setSelectedProtocole(protocole)}
                        data-testid={`protocole-${protocole.id}`}
                      >
                        <CardContent className="pt-6">
                          <h3 className="font-semibold text-lg text-gray-900 mb-3">{protocole.nom}</h3>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="text-gray-600">Vitamines:</span>
                              {protocole.vitamines && protocole.vitamines.length > 0 ? (
                                <ul className="ml-4 mt-1 space-y-1">
                                  {protocole.vitamines.slice(0, 4).map((vit, i) => (
                                    <li key={i} className="text-gray-700">• {vit}</li>
                                  ))}
                                  {protocole.vitamines.length > 4 && (
                                    <li className="text-gray-500 italic">
                                      ... et {protocole.vitamines.length - 4} autres
                                    </li>
                                  )}
                                </ul>
                              ) : (
                                <span className="ml-2 text-gray-500">Non spécifié</span>
                              )}
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t">
                              <span className="text-gray-600">Durée:</span>
                              <span className="font-medium text-gray-900">{protocole.duree} min</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Débit:</span>
                              <span className="font-medium text-gray-900">{protocole.debit}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Protocole personnalisé */}
              <TabsContent value="custom" className="mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="custom-nom">Nom du Protocole *</Label>
                    <Input
                      id="custom-nom"
                      placeholder="Ex: Mon Protocole Personnalisé"
                      value={customProtocole.nom}
                      onChange={(e) => setCustomProtocole({...customProtocole, nom: e.target.value})}
                    />
                  </div>

                  {/* Sélection des vitamines avec recherche */}
                  <div className="space-y-2">
                    <Label>Vitamines et Nutriments *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Rechercher une vitamine..."
                        value={searchVitamine}
                        onChange={(e) => setSearchVitamine(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {/* Suggestions */}
                    {searchVitamine && filteredVitamines.length > 0 && (
                      <div className="max-h-40 overflow-y-auto border rounded-md bg-white shadow-sm">
                        {filteredVitamines.slice(0, 8).map((vit, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => addVitamine(vit)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-b-0"
                          >
                            {vit}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Vitamines sélectionnées */}
                    {customProtocole.vitamines.length > 0 && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Vitamines sélectionnées ({customProtocole.vitamines.length}):
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {customProtocole.vitamines.map((vit, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                            >
                              {vit}
                              <button
                                type="button"
                                onClick={() => removeVitamine(vit)}
                                className="ml-2 hover:text-blue-900"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="custom-dosage">Dosage</Label>
                    <select
                      id="custom-dosage"
                      value={customProtocole.dosage}
                      onChange={(e) => setCustomProtocole({...customProtocole, dosage: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Standard">Standard</option>
                      <option value="Haute dose">Haute dose</option>
                      <option value="Faible dose">Faible dose</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="custom-debit">Débit (mL/h)</Label>
                      <Input
                        id="custom-debit"
                        type="number"
                        min="50"
                        max="300"
                        value={customProtocole.debit}
                        onChange={(e) => setCustomProtocole({...customProtocole, debit: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom-duree">Durée (minutes)</Label>
                      <Input
                        id="custom-duree"
                        type="number"
                        min="15"
                        max="180"
                        value={customProtocole.duree}
                        onChange={(e) => setCustomProtocole({...customProtocole, duree: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !selectedPatient || (protocoleMode === 'predefined' && !selectedProtocole) || (protocoleMode === 'custom' && (!customProtocole.nom || customProtocole.vitamines.length === 0))}
              data-testid={isEditing ? "update-infusion-button" : "create-infusion-button"}
              style={{
                background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
                color: 'white'
              }}
            >
              {loading 
                ? (isEditing ? 'Modification...' : 'Création...') 
                : (isEditing ? 'Modifier la perfusion' : 'Créer la perfusion')
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InfusionFormDialog;
